import { Controller } from "@hotwired/stimulus"

// Every right-hand sidebar can be shown three ways, like Messages: docked as
// a sidebar (the default, as the page lays it out), as a drawer over the
// page, or as a floating window you can drag. This controller sits on
// <body> and adds a view button to each .detail-sidebar's action bar —
// server-rendered or appended later (contact and message detail panels) —
// and remembers the choice per kind of sidebar (Jane, files, a contact...).
const MODES = [
  { key: "sidebar", label: "Sidebar", icon: "bi-layout-sidebar-reverse" },
  { key: "drawer", label: "Drawer", icon: "bi-layout-sidebar-inset-reverse" },
  { key: "floating", label: "Floating window", icon: "bi-window-stack" }
]
const STORAGE_PREFIX = "sidebar_view:"

export default class extends Controller {
  connect() {
    this._scan()
    this._observer = new MutationObserver(() => {
      if (this._pending) return
      this._pending = requestAnimationFrame(() => { this._pending = null; this._scan() })
    })
    // A panel closed (or swapped out) while full screen takes its full screen with it: the page
    // behind it was hidden and inert, and would stay that way.
    this._observer.observe(document.body, { childList: true, subtree: true })
    // One delegated listener, not one per button: sidebars re-render and
    // clone their action bars, and a clone keeps the markup but not listeners.
    this._onDocClick = (event) => this._click(event)
    document.addEventListener("click", this._onDocClick)
    // Capture phase, and the first Escape only leaves full screen: the panel's own Escape
    // (which closes it) must not fire on the same press.
    this._onEscape = event => {
      if (event.key !== "Escape" || !this._fullscreenAside) return
      event.stopImmediatePropagation()
      this._exitFullscreen()
    }
    this._onClosed = event => { if (event.detail.sidebar === this._fullscreenAside) this._exitFullscreen() }
    document.addEventListener("keydown", this._onEscape, true)
    window.addEventListener("sidebar:closed", this._onClosed)
  }

  disconnect() {
    this._exitFullscreen()
    document.removeEventListener("keydown", this._onEscape, true)
    window.removeEventListener("sidebar:closed", this._onClosed)
    this._observer?.disconnect()
    document.removeEventListener("click", this._onDocClick)
  }

  _click(event) {
    const item = event.target.closest(".sidebar-view-item")
    const button = event.target.closest(".sidebar-view-btn")
    const aside = event.target.closest(".detail-sidebar, .needs-you-pane")
    if (item && aside) {
      this._apply(aside, item.dataset.mode)
      this._closeMenus()
      return
    }
    if (button && aside) {
      const panel = button.parentElement.querySelector(".sidebar-view-panel")
      const open = !panel.classList.contains("is-open")
      this._closeMenus()
      panel.classList.toggle("is-open", open)
      button.setAttribute("aria-expanded", String(open))
      return
    }
    if (!event.target.closest(".sidebar-view-menu")) this._closeMenus()
  }

  _scan() {
    if (this._fullscreenAside && !this._fullscreenAside.isConnected) this._exitFullscreen()
    document.querySelectorAll(".detail-sidebar, .needs-you-pane").forEach((aside) => {
      const actions = aside.classList.contains("needs-you-pane")
        ? aside.querySelector(".offcanvas-header")
        : aside.querySelector(":scope > .detail-sidebar-actions")
      if (!actions) return
      if (!actions.querySelector(".sidebar-view-menu")) {
        const menu = this._menu()
        const close = actions.querySelector(".btn-close")
        const overflow = actions.querySelector('.msg-detail-menu, .jane-sidebar-static-menu, .contact-detail-menu')
        if (overflow) overflow.after(menu)
        else if (close) actions.insertBefore(menu, close)
        else actions.prepend(menu)
      }
      // Every right-hand panel can fill the screen, not only Jane's: messages, contacts, a job.
      if (aside.classList.contains("detail-sidebar") && !aside.querySelector(".sidebar-fullscreen-btn")) {
        const view = actions.querySelector(".sidebar-view-menu")
        const button = this._fullscreenButtonEl()
        if (view) view.after(button)
        else actions.prepend(button)
      }
      if (aside.dataset.sidebarView === undefined) this._apply(aside, this._saved(aside), false)
    })
  }

  // The same button Jane's own sidebar renders (shared/_jane_sidebar).
  _fullscreenButtonEl() {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "detail-sidebar-action sidebar-fullscreen-btn"
    button.title = "Expand to full screen"
    button.setAttribute("aria-label", "Expand to full screen")
    button.setAttribute("aria-pressed", "false")
    button.dataset.action = "sidebar-views#toggleFullscreen"
    button.innerHTML = '<i class="bi bi-arrows-angle-expand" aria-hidden="true"></i>'
    return button
  }

  // "jane" for .detail-sidebar--jane, and so on.
  _key(aside) {
    if (aside.classList.contains("needs-you-pane")) return "needs-you"
    const modifier = [...aside.classList].find((c) => /^detail-sidebar--(?!as-)/.test(c))
    return modifier ? modifier.replace("detail-sidebar--", "") : "panel"
  }

  _saved(aside) {
    try {
      const mode = window.localStorage.getItem(STORAGE_PREFIX + this._key(aside))
      return MODES.some((m) => m.key === mode) ? mode : "sidebar"
    } catch (_error) {
      return "sidebar"
    }
  }

  _apply(aside, mode, remember = true) {
    if (aside === this._fullscreenAside) this._exitFullscreen()
    aside.dataset.sidebarView = mode
    const base = aside.classList.contains("needs-you-pane") ? "needs-you-pane" : "detail-sidebar"
    aside.classList.toggle(`${base}--as-drawer`, mode === "drawer")
    aside.classList.toggle(`${base}--as-floating`, mode === "floating")
    aside.style.left = ""
    aside.style.top = ""
    aside.style.right = ""
    aside.querySelectorAll(".sidebar-view-item").forEach((item) => {
      item.setAttribute("aria-checked", String(item.dataset.mode === mode))
    })
    if (mode === "floating") this._draggable(aside)
    if (remember) {
      try { window.localStorage.setItem(STORAGE_PREFIX + this._key(aside), mode) } catch (_error) { /* private mode */ }
    }
    window.dispatchEvent(new CustomEvent("jane:panels-changed"))
  }

  toggleFullscreen(event) {
    event.preventDefault()
    const aside = event.currentTarget.closest('.detail-sidebar')
    if (this._fullscreenAside === aside) return this._exitFullscreen()
    this._exitFullscreen()
    this._fullscreenAside = aside
    this._fullscreenButton = event.currentTarget
    this._fullscreenHidden = []
    let child = aside
    while (child.parentElement && child !== document.body) {
      for (const sibling of child.parentElement.children) {
        if (sibling === child || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue
        this._fullscreenHidden.push([sibling, sibling.style.visibility, sibling.inert])
        sibling.style.visibility = 'hidden'
        sibling.inert = true
      }
      child = child.parentElement
    }
    aside.classList.add('detail-sidebar--fullscreen')
    document.documentElement.classList.add('sidebar-fullscreen')
    this._fullscreenButton.setAttribute('aria-pressed', 'true')
    this._fullscreenButton.setAttribute('aria-label', 'Exit full screen')
    this._fullscreenButton.title = 'Exit full screen'
    this._fullscreenButton.querySelector('i').className = 'bi bi-arrows-angle-contract'
  }

  _exitFullscreen() {
    if (!this._fullscreenAside) return
    this._fullscreenAside.classList.remove('detail-sidebar--fullscreen')
    document.documentElement.classList.remove('sidebar-fullscreen')
    this._fullscreenHidden.forEach(([element, visibility, inert]) => { element.style.visibility = visibility; element.inert = inert })
    this._fullscreenButton.setAttribute('aria-pressed', 'false')
    this._fullscreenButton.setAttribute('aria-label', 'Expand to full screen')
    this._fullscreenButton.title = 'Expand to full screen'
    this._fullscreenButton.querySelector('i').className = 'bi bi-arrows-angle-expand'
    this._fullscreenButton.focus()
    this._fullscreenAside = null
  }

  _menu() {
    const wrap = document.createElement("div")
    wrap.className = "sidebar-view-menu"
    const button = document.createElement("button")
    button.type = "button"
    button.className = "detail-sidebar-action sidebar-view-btn"
    button.title = "View as"
    button.setAttribute("aria-label", "View as")
    button.setAttribute("aria-haspopup", "true")
    button.innerHTML = '<i class="bi bi-window-sidebar" aria-hidden="true"></i>'
    const panel = document.createElement("div")
    panel.className = "sidebar-view-panel"
    panel.setAttribute("role", "menu")
    const label = document.createElement("p")
    label.className = "sidebar-view-label"
    label.textContent = "View as"
    panel.appendChild(label)
    MODES.forEach((mode) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "sidebar-view-item"
      item.dataset.mode = mode.key
      item.setAttribute("role", "menuitemradio")
      item.innerHTML = `<i class="bi ${mode.icon}" aria-hidden="true"></i><span></span><i class="bi bi-check2 sidebar-view-check" aria-hidden="true"></i>`
      item.querySelector("span").textContent = mode.label
      panel.appendChild(item)
    })
    wrap.append(button, panel)
    return wrap
  }

  _closeMenus() {
    document.querySelectorAll(".sidebar-view-panel.is-open").forEach((panel) => {
      panel.classList.remove("is-open")
      panel.previousElementSibling?.setAttribute("aria-expanded", "false")
    })
  }

  // A floating sidebar moves by its frame: the margin around the card and
  // the action bar, never the content.
  _draggable(aside) {
    if (aside._sidebarViewsDrag) return
    aside._sidebarViewsDrag = true
    aside.addEventListener("pointerdown", (event) => {
      if (aside.dataset.sidebarView !== "floating" || event.button !== 0) return
      const onFrame = event.target === aside || (event.target.closest(".detail-sidebar-actions, .offcanvas-header") && !event.target.closest("button, a, input, .sidebar-view-panel"))
      if (!onFrame) return
      const rect = aside.getBoundingClientRect()
      const offset = { x: event.clientX - rect.left, y: event.clientY - rect.top }
      const move = (e) => {
        const left = Math.min(Math.max(e.clientX - offset.x, 8), window.innerWidth - rect.width - 8)
        const top = Math.min(Math.max(e.clientY - offset.y, 8), window.innerHeight - 48)
        aside.style.right = "auto"
        aside.style.left = `${left}px`
        aside.style.top = `${top}px`
      }
      const up = () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", up)
      }
      window.addEventListener("pointermove", move)
      window.addEventListener("pointerup", up)
      event.preventDefault()
    })
  }
}
